import { Column, Id, ReactGrid, Row } from "@silevis/reactgrid";
import React, { useEffect, useMemo, useState } from "react";
import { SortableHeaderCellTemplate } from "../CellTemplates/SortableHeaderCell";
import { SummaryCellTemplate } from "../CellTemplates/SummaryCell";
import ChangeAmount from "../ChangeAmount/ChangeAmount";
import ContextMenu from "../ContextMenu/ContextMenu";
import { useSelectedRows } from "../Contexts/SelectedRowsProvider";
import FilterMenu from "../FilterMenu/FilterMenu";
import SortMenu from "../SortMenu/SortMenu";
import "./table.css";
import {
  getCellValue,
  isCheckboxCell,
  isNumberCell,
  isTextCell,
  numberFilterOptions,
  textFilterOptions,
} from "./TableHelpers";

const defaultFilterOption = { value: "equals", label: "Equals" };

interface BaseTableProps {
  columns: Column[];
  setColumns: (cols: Column[]) => void;
  rows: Row[];
  stickyLeftColumns?: number;
  onCellsChanged: (changedCells: any) => void;
  modifyCellValues: (
    modifyBy: number,
    operation: "multiply" | "add" | "fixed",
    range: any[]
  ) => void;
  customCellTemplates?: Record<string, any>;
  historyTable?: boolean;
}

const BaseTable: React.FC<BaseTableProps> = ({
  columns,
  setColumns,
  rows: initialRows,
  stickyLeftColumns = 1,
  onCellsChanged,
  modifyCellValues,
  customCellTemplates,
  historyTable,
}) => {
  const [selectedCell, setSelectedCell] = useState<any>(undefined);
  const [selectedColumn, setSelectedColumn] = useState<any>(undefined);
  const [selectedRange, setSelectedRange] = useState<any[]>([]);
  const { setSelectedRows } = useSelectedRows();

  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);

  const [isAddAmountVisible, setIsAddAmountVisible] = useState(false);
  const [addAmountType, setAddAmountType] = useState<
    "multiply" | "add" | undefined
  >();
  const [addAmountHeader, setAddAmountHeader] = useState<string>();
  const [addAmountValue, setAddAmountValue] = useState<number | undefined>();

  const [isSortMenuVisible, setIsSortMenuVisible] = useState(false);
  const [sortMenuPosition, setSortMenuPosition] = useState({ x: 0, y: 0 });
  const [sortParams, setSortParams] = useState<{
    columnId?: string;
    sortOrder?: "ascending" | "descending";
  }>({});

  const [isFilterMenuVisible, setIsFilterMenuVisible] = useState(false);
  const [filterMenuPosition, setFilterMenuPosition] = useState({ x: 0, y: 0 });
  const [filterParams, setFilterParams] = useState<{
    columnId?: string;
    filterOption?: { value: string; label: string };
    filterValue?: string | number;
  }>({});
  const [filterOption, setFilterOption] = useState(defaultFilterOption);
  const [filterOptions, setFilterOptions] = useState(numberFilterOptions);
  const [filterValue, setFilterValue] = useState<string | number>("");

  useEffect(() => {
    setIsContextMenuVisible(false);
    setIsSortMenuVisible(false);
    setIsFilterMenuVisible(false);
    setIsAddAmountVisible(false);
    setSelectedCell(undefined);
    setSelectedColumn(undefined);
    setAddAmountValue(undefined);
    setFilterValue("");
    setFilterOption(defaultFilterOption);
    setSortParams({});
    setFilterParams({});
  }, [initialRows]);

  const handleColumnResized = (ci: Id, width: number) => {
    setColumns(
      columns.map((col) => (col.columnId === ci ? { ...col, width } : col))
    );
  };

  const headerRow = useMemo(
    () => initialRows.find((row) => row.rowId === "header"),
    [initialRows]
  );
  const summaryRow = useMemo(
    () => initialRows.find((row) => row.rowId === "summary"),
    [initialRows]
  );
  const dataRows = useMemo(
    () =>
      initialRows.filter(
        (row) => row.rowId !== "header" && row.rowId !== "summary"
      ),
    [initialRows]
  );

  const filterRows = (
    rows: Row[],
    columnId: string,
    filterOption: { value: string; label: string },
    filterValue: string | number
  ) => {
    return rows.filter((row) => {
      const cell = row.cells?.find((c: any) => c.groupId === columnId);
      if (!cell) return false;

      if (isNumberCell(cell)) {
        const n = Number(filterValue);
        if (!Number.isFinite(n)) return false;
        switch (filterOption.value) {
          case "equals":
            return cell.value === n;
          case "morethan":
            return cell.value > n;
          case "lessthan":
            return cell.value < n;
          default:
            return false;
        }
      }

      if (isTextCell(cell)) {
        switch (filterOption.value) {
          case "equals":
            return cell.text === String(filterValue);
          case "contains":
            return cell.text
              .toLowerCase()
              .includes(String(filterValue).toLowerCase());
          default:
            return false;
        }
      }
      return false;
    });
  };

  const sortRows = (
    rows: Row[],
    columnId: string,
    sortOrder: "ascending" | "descending"
  ) => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      const cellA = a.cells?.find((c: any) => c.groupId === columnId);
      const cellB = b.cells?.find((c: any) => c.groupId === columnId);

      let valueA: string | number = getCellValue(cellA);
      let valueB: string | number = getCellValue(cellB);

      if (typeof valueA === "string" && typeof valueB === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (valueA == valueB) return 0;
      const order = sortOrder === "ascending" ? 1 : -1;
      return valueA > valueB ? order : -order;
    });
    return sorted;
  };

  const displayedRows = useMemo(() => {
    let filtered = dataRows;

    if (
      filterParams.columnId &&
      filterParams.filterOption &&
      filterParams.filterValue !== undefined &&
      filterParams.filterValue !== ""
    ) {
      filtered = filterRows(
        dataRows,
        filterParams.columnId,
        filterParams.filterOption,
        filterParams.filterValue
      );
    }

    if (sortParams.columnId && sortParams.sortOrder) {
      filtered = sortRows(filtered, sortParams.columnId, sortParams.sortOrder);
    }

    let rowsOut: Row[] = [];
    if (headerRow) rowsOut.push(headerRow);
    rowsOut = rowsOut.concat(filtered);
    if (summaryRow) rowsOut.push(summaryRow);
    return rowsOut;
  }, [dataRows, headerRow, summaryRow, filterParams, sortParams]);

  const getColumnType = (columnId: string) => {
    for (const row of dataRows) {
      const cell = row.cells?.find((c: any) => c.groupId === columnId);
      if (isNumberCell(cell)) return "number";
      if (isTextCell(cell)) return "string";
      if (isCheckboxCell(cell)) return "checkbox";
    }
    return "string";
  };

  const handleContextMenu = (event: any) => {
    event.preventDefault();
    const dataset = event.target.dataset;
    const colIdx = Number(dataset.cellColidx);
    const rowIdx = Number(dataset.cellRowidx) - 1;

    if (dataset.cellRowidx === "0") {
      setSortMenuPosition({ x: event.pageX, y: event.pageY });
      setSelectedColumn(columns[colIdx]);
      setIsSortMenuVisible(true);
      setIsContextMenuVisible(false);
      return;
    }

    let isAlreadySelected = false;
    if (selectedRange && selectedRange.length) {
      for (const range of selectedRange) {
        const { rows, columns } = range;
        if (
          rows.some((r: any) => r.rowId === rowIdx) &&
          columns.some((c: any) => c?.idx === colIdx) &&
          selectedRange[0].columns.length > 1 &&
          selectedRange[0].rows.length >= 1
        ) {
          isAlreadySelected = true;
          break;
        }
      }

      if (!isAlreadySelected) {
        setSelectedCell({ columnId: columns[colIdx].columnId, rowId: rowIdx });
        setSelectedRange([
          {
            rows: [initialRows[rowIdx + 1]],
            columns: [columns[colIdx]],
          },
        ]);
      }
      setMenuPosition({ x: event.pageX, y: event.pageY });
      setIsContextMenuVisible(true);
      setIsSortMenuVisible(false);
    }
  };

  const handleContainerClick = (e: any) => {
    e.preventDefault();
    setIsContextMenuVisible(false);
    setIsSortMenuVisible(false);
    setIsFilterMenuVisible(false);
    setIsAddAmountVisible(false);
    setAddAmountValue(undefined);
    setFilterOption(defaultFilterOption);
    setFilterValue("");
  };

  const handleAddAmount = (operation: "multiply" | "add") => {
    setAddAmountHeader(
      operation === "multiply" ? "Multiply by amount" : "Add static sum"
    );
    setAddAmountType(operation);
    setIsAddAmountVisible(true);
    setIsContextMenuVisible(false);
  };

  const handleAddAmountApply = () => {
    if (addAmountType && addAmountValue !== undefined) {
      modifyCellValues(addAmountValue, addAmountType, selectedRange);
    }
    setAddAmountValue(undefined);
    setIsAddAmountVisible(false);
  };

  const handleSelectionChange = (newSelection: any) => {
    setSelectedRange(newSelection);
    setSelectedRows(newSelection);
  };

  const handleSort = (order: "ascending" | "descending") => {
    if (!selectedColumn) return;
    setSortParams({
      columnId: selectedColumn.columnId,
      sortOrder: order,
    });
    setIsSortMenuVisible(false);
  };

  const handleFilterOptions = (columnId: string) => {
    const colType = getColumnType(columnId);
    if (colType === "number") {
      setFilterOptions(numberFilterOptions);
      setFilterOption(numberFilterOptions[0]);
    } else {
      setFilterOptions(textFilterOptions);
      setFilterOption(textFilterOptions[0]);
    }
  };

  const handleFilterApply = () => {
    if (!selectedColumn) return;
    setFilterParams({
      columnId: selectedColumn.columnId,
      filterOption,
      filterValue,
    });
    setIsFilterMenuVisible(false);
    setIsSortMenuVisible(false);
  };

  const handleFilterClear = () => {
    setFilterParams({});
    setFilterValue("");
    setIsFilterMenuVisible(false);
    setIsSortMenuVisible(false);
  };

  const handleFocusLocationChanging = (location: any) => {
    const isFirstColumn =
      columns.length > 0 && location.columnId === columns[0].columnId;

    if (location.rowId === "header" || location.rowId === "summary") {
      return true;
    }

    if (isFirstColumn && typeof location.rowId === "number") {
      setSelectedCell(location);
      setSelectedRange([
        {
          columnRange: { start: 0, end: columns.length - 1 },
          rowRange: { start: location.rowId, end: location.rowId },
        },
      ]);
      return true;
    } else {
      setSelectedCell(location);
      setSelectedRange([]);
      return true;
    }
  };

  return (
    <div
      className="tableContainer"
      style={{ height: historyTable ? "calc(100% - 50px)" : "100%" }}
      onContextMenu={handleContextMenu}
      onClick={handleContainerClick}
      tabIndex={0}
    >
      {isContextMenuVisible && (
        <ContextMenu
          x={menuPosition.x}
          y={menuPosition.y}
          multiplication={(e: any) => {
            handleAddAmount("multiply");
            setIsAddAmountVisible(true);
            setIsContextMenuVisible(false);
            e.stopPropagation();
          }}
          add={(e: any) => {
            handleAddAmount("add");
            setIsAddAmountVisible(true);
            setIsContextMenuVisible(false);
            e.stopPropagation();
          }}
        />
      )}
      {isAddAmountVisible && (
        <ChangeAmount
          header={addAmountHeader}
          x={menuPosition.x}
          y={menuPosition.y}
          value={addAmountValue}
          onInputChange={(event: any) => {
            setAddAmountValue(Number(event.target.value));
          }}
          handleClick={(e: Event) => e.stopPropagation()}
          onApply={handleAddAmountApply}
        />
      )}
      {isSortMenuVisible && (
        <SortMenu
          x={sortMenuPosition.x}
          y={sortMenuPosition.y}
          ascend={() => handleSort("ascending")}
          descend={() => handleSort("descending")}
          filter={(e: any) => {
            setIsSortMenuVisible(false);
            setIsFilterMenuVisible(true);
            setFilterMenuPosition({
              x: sortMenuPosition.x,
              y: sortMenuPosition.y,
            });
            if (selectedColumn) handleFilterOptions(selectedColumn.columnId);
            e.stopPropagation();
          }}
        />
      )}
      {isFilterMenuVisible && (
        <FilterMenu
          x={filterMenuPosition.x}
          y={filterMenuPosition.y}
          onApply={handleFilterApply}
          onClear={handleFilterClear}
          onFilterChange={setFilterOption}
          inputValue={filterValue}
          onInputChange={(event: any) => {
            setFilterValue(event.target.value);
          }}
          filterOptions={filterOptions}
        />
      )}
      {columns.length !== 0 && displayedRows.length !== 0 && (
        <ReactGrid
          rows={displayedRows}
          columns={columns}
          stickyLeftColumns={stickyLeftColumns}
          stickyTopRows={1}
          customCellTemplates={
            customCellTemplates ?? {
              sortableHeader: new SortableHeaderCellTemplate(),
              summary: new SummaryCellTemplate(),
            }
          }
          focusLocation={selectedCell}
          onFocusLocationChanging={handleFocusLocationChanging}
          onCellsChanged={onCellsChanged}
          onSelectionChanged={handleSelectionChange}
          enableRangeSelection={true}
          enableRowSelection={true}
          onColumnResized={handleColumnResized}
        />
      )}
    </div>
  );
};

export default BaseTable;
